/**
 * A `Wire` made of a data channel between two browsers.
 *
 * The mirror of `server/socket.ts`, which is the same job over a WebSocket.
 * Everything above these two files - the router, the realms, the whole of
 * the game's side of the wire - speaks `Wire` and cannot tell which of them
 * it is standing on. That was the point of the four steps before this one.
 *
 * **Messages are JSON and chunks are bytes**, exactly as on our server, and
 * for the same reasons: a chunk is four thousand and ninety six numbers and
 * a message is small enough that being readable in devtools is worth more
 * than the bytes.
 *
 * **Ordered and reliable**, which is the default and is left alone. A voxel
 * world is a stream of edits where every one matters and "the block is gone"
 * arriving before "the block is there" is a hole nobody can fill. Chunks are
 * large and a browser will refuse to send one in a single go, so they go in
 * slices with a header - see `sendChunk`.
 */
import type { Wire } from "../core/net/link.js";
import { encode, decode } from "../core/net/protocol.js";
import { SnapshotGate } from "../core/net/gate.js";

/**
 * The most a data channel will take in one send.
 *
 * Sixteen kilobytes is what every browser agrees on; larger is allowed by
 * the standard and fails in practice. A chunk is eight kilobytes of Uint16
 * plus its header, so most go whole and the slicing is for the ones that do
 * not - and for the day a chunk gets bigger.
 */
const SLICE = 16 * 1024;

/**
 * How much may be waiting to go out before we stop pushing.
 *
 * A data channel buffers what it cannot send yet, and a page that keeps
 * pushing into a slow link fills memory until the tab dies. Above this, the
 * chunk stream waits for the drain event rather than adding to the pile:
 * a world arriving slowly is a world arriving.
 */
const HIGH_WATER = 1024 * 1024;

export function channelWire(channel: RTCDataChannel): Wire {
  channel.binaryType = "arraybuffer";
  channel.bufferedAmountLowThreshold = HIGH_WATER / 2;

  // The same picture is not sent twice. See core/net/gate.
  const gate = new SnapshotGate();
  const wire: Wire = {
    onMessage: null, onChunk: null, onClose: null,
    open: () => channel.readyState === "open",
    send(message: object): void {
      wire.sendEncoded(message, encode(message as never));
    },
    sendEncoded(message: object, text: string): void {
      if (channel.readyState !== "open") return;
      if (!gate.lets(String((message as { t?: string }).t ?? ""), text, Date.now())) return;
      try {
        channel.send(text);
      } catch {
        // A channel that has gone between the check and the send. Somebody
        // leaving mid-tick is ordinary and the tick has to finish.
      }
    },
    sendChunk(frame: ArrayBuffer): void {
      if (channel.readyState !== "open") return;
      void push(channel, frame);
    },
    close(): void {
      /*
       * Not while something is still on its way out. A world refuses
       * somebody by sending the reason and closing, and closing at once
       * could drop the reason with the channel: the guest saw only a
       * closed door and said "не відповідає" instead of "не той пароль".
       * Waits for the buffer to empty, and not more than a second.
       */
      const started = Date.now();
      const shut = (): void => {
        if (channel.bufferedAmount > 0 && Date.now() - started < 1000) {
          setTimeout(shut, 50);
          return;
        }
        setTimeout(() => { try { channel.close(); } catch { /* already gone */ } }, 100);
      };
      shut();
    },
  };

  channel.addEventListener("message", (event) => {
    const data = (event as MessageEvent).data;
    if (data instanceof ArrayBuffer) {
      const whole = gather(data);
      if (whole !== null) wire.onChunk?.(whole);
      return;
    }
    if (typeof data !== "string") return;
    const message = decode(data);
    if (message !== null) wire.onMessage?.(message);
  });
  channel.addEventListener("close", () => { wire.onClose?.(); });
  channel.addEventListener("error", () => { wire.onClose?.(); });

  /*
   * Chunks in slices, with a four-byte header saying which of how many.
   *
   * A data channel is ordered and reliable, so the pieces arrive in order
   * and none is lost - which means reassembly is a counter and a buffer
   * rather than a protocol. The header exists so that the far end knows
   * when it has the whole thing without being told the length twice.
   */
  const waiting: Uint8Array[] = [];
  let expecting = 0;

  function gather(slice: ArrayBuffer): ArrayBuffer | null {
    if (slice.byteLength < 4) return null;
    const head = new DataView(slice);
    const index = head.getUint16(0);
    const total = head.getUint16(2);
    if (index === 0) {
      waiting.length = 0;
      expecting = total;
    } else if (total !== expecting || index !== waiting.length) {
      // Out of step: a stream we cannot make sense of is dropped whole
      // rather than handed on as a world with a hole in it.
      waiting.length = 0;
      expecting = 0;
      return null;
    }
    waiting.push(new Uint8Array(slice, 4));
    if (waiting.length < expecting) return null;
    const size = waiting.reduce((n, part) => n + part.byteLength, 0);
    const whole = new Uint8Array(size);
    let at = 0;
    for (const part of waiting) {
      whole.set(part, at);
      at += part.byteLength;
    }
    waiting.length = 0;
    expecting = 0;
    return whole.buffer;
  }

  return wire;
}

/** Push one frame out, in slices, waiting when the far end is behind. */
async function push(channel: RTCDataChannel, frame: ArrayBuffer): Promise<void> {
  const body = SLICE - 4;
  const total = Math.max(1, Math.ceil(frame.byteLength / body));
  for (let index = 0; index < total; index++) {
    if (channel.readyState !== "open") return;
    if (channel.bufferedAmount > HIGH_WATER) await drained(channel);
    if (channel.readyState !== "open") return;
    const part = new Uint8Array(frame, index * body,
      Math.min(body, frame.byteLength - index * body));
    const slice = new Uint8Array(4 + part.byteLength);
    const head = new DataView(slice.buffer);
    head.setUint16(0, index);
    head.setUint16(2, total);
    slice.set(part, 4);
    try {
      channel.send(slice.buffer);
    } catch {
      return;
    }
  }
}

function drained(channel: RTCDataChannel): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      channel.removeEventListener("bufferedamountlow", done);
      resolve();
    };
    channel.addEventListener("bufferedamountlow", done);
    // And a ceiling on the wait, so a channel that never drains does not
    // hang the world's chunk stream for ever.
    setTimeout(done, 5000);
  });
}
